import type {
  CreateOfferRequest,
  OfferDetailResponse,
  UpdateOfferRequest,
} from "@codway-dev/shared";
import { stripShopifyGid } from "@codway-dev/shared";

// ─── Types ────────────────────────────────────────────────────────────────────

export type QuantityTiersPricingAction =
  | "none"
  | "fixed_price_per_item"
  | "percentage_off"
  | "fixed_amount_off";

export interface QuantityTiersTargetProductVariant {
  id: string;
  title: string;
  imageUrl?: string;
}

export interface QuantityTiersTargetProduct {
  id: string;
  title: string;
  imageUrl?: string;
  price: string;
  compareAtPrice?: string;
  defaultVariantId?: string;
  variants?: QuantityTiersTargetProductVariant[];
}

export interface QuantityTierEditorTier {
  _tempId: string;
  quantity: number;
  title: string;
  isDefault: boolean;
  hideCompareAtPrice: boolean;
  pricingAction: QuantityTiersPricingAction;
  // Used for fixed_price_per_item and fixed_amount_off
  pricingAmount: number;
  // Used for percentage_off
  pricingPercentage: number;
  badgeText: string;
  badgeTextColor: string;
  badgeBackgroundColor: string;
  priceColor: string;
  titleColor: string;
  customImageUrl: string;
}

export interface QuantityTiersEditorState {
  internalName: string;
  isActive: boolean;

  targetProduct: QuantityTiersTargetProduct | null;

  tiers: QuantityTierEditorTier[];

  layout: "horizontal" | "grid";
  activeBorderColor: string;
  backgroundColor: string;
  selectedBackgroundColor: string;
  borderRadius: number;

  hideProductImage: boolean;
  hideVariantQuantitySelectors: boolean;
  disableVariantSelectors: boolean;
  useCompareAtPriceAsBase: boolean;
  disableIfExceedsMaxQuantity: boolean;
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

export function getDefaultNewTier(): QuantityTierEditorTier {
  return {
    _tempId: crypto.randomUUID(),
    quantity: 1,
    title: "Nueva oferta",
    isDefault: false,
    hideCompareAtPrice: false,
    pricingAction: "none",
    pricingAmount: 0,
    pricingPercentage: 0,
    badgeText: "",
    badgeTextColor: "#ffffff",
    badgeBackgroundColor: "#000000",
    priceColor: "#1f2937",
    titleColor: "#111827",
    customImageUrl: "",
  };
}

export function getDefaultQuantityTiersState(): QuantityTiersEditorState {
  return {
    internalName: "Nueva oferta",
    isActive: true,
    targetProduct: null,
    tiers: [
      {
        _tempId: crypto.randomUUID(),
        quantity: 1,
        title: "Compra 1 unidad",
        isDefault: true,
        hideCompareAtPrice: false,
        pricingAction: "none",
        pricingAmount: 0,
        pricingPercentage: 0,
        badgeText: "",
        badgeTextColor: "#ffffff",
        badgeBackgroundColor: "#000000",
        priceColor: "#1f2937",
        titleColor: "#111827",
        customImageUrl: "",
      },
      {
        _tempId: crypto.randomUUID(),
        quantity: 2,
        title: "¡Compra 2 unidades con un 25% de descuento!",
        isDefault: false,
        hideCompareAtPrice: false,
        pricingAction: "percentage_off",
        pricingAmount: 0,
        pricingPercentage: 25,
        badgeText: "Ahorra 25%",
        badgeTextColor: "#ffffff",
        badgeBackgroundColor: "#000000",
        priceColor: "#1f2937",
        titleColor: "#111827",
        customImageUrl: "",
      },
    ],
    layout: "horizontal",
    activeBorderColor: "rgba(0,116,191,1)",
    backgroundColor: "rgba(217,235,246,1)",
    selectedBackgroundColor: "rgba(255,255,255,1)",
    borderRadius: 8,

    hideProductImage: false,
    hideVariantQuantitySelectors: false,
    disableVariantSelectors: false,
    useCompareAtPriceAsBase: false,
    disableIfExceedsMaxQuantity: false,
  };
}

// ─── Builders ─────────────────────────────────────────────────────────────────

function buildTarget(state: QuantityTiersEditorState) {
  if (!state.targetProduct) {
    throw new Error("Selecciona un producto antes de guardar la oferta.");
  }
  return {
    type: "shopify_product" as const,
    shopify_product_id: state.targetProduct.id,
    shopify_variant_id: state.targetProduct.defaultVariantId,
    display_meta: {
      title: state.targetProduct.title,
      image_url: state.targetProduct.imageUrl ?? undefined,
      price: state.targetProduct.price,
      compare_at_price: state.targetProduct.compareAtPrice || undefined,
      variants: (state.targetProduct.variants ?? []).map((v) => ({
        id: v.id,
        title: v.title,
        ...(v.imageUrl ? { image_url: v.imageUrl } : {}),
      })),
    },
  };
}

function buildTier(tier: QuantityTierEditorTier) {
  type PricingLogic =
    | { action: "none" }
    | { action: "fixed_price_per_item"; amount: number }
    | { action: "percentage_off"; value: number }
    | { action: "fixed_amount_off"; amount: number };

  let pricing_logic: PricingLogic;
  if (tier.pricingAction === "fixed_price_per_item") {
    pricing_logic = {
      action: "fixed_price_per_item",
      amount: tier.pricingAmount,
    };
  } else if (tier.pricingAction === "percentage_off") {
    pricing_logic = { action: "percentage_off", value: tier.pricingPercentage };
  } else if (tier.pricingAction === "fixed_amount_off") {
    pricing_logic = { action: "fixed_amount_off", amount: tier.pricingAmount };
  } else {
    pricing_logic = { action: "none" };
  }

  return {
    quantity: tier.quantity,
    is_default: tier.isDefault,
    title: tier.title,
    hide_compare_at_price: tier.hideCompareAtPrice,
    pricing_logic,
    badge: tier.badgeText
      ? {
          text: tier.badgeText,
          text_color: tier.badgeTextColor || undefined,
          background_color: tier.badgeBackgroundColor || undefined,
        }
      : null,
    price_color: tier.priceColor || undefined,
    title_color: tier.titleColor || undefined,
    custom_image_url: tier.customImageUrl || undefined,
  };
}

function buildDisplayData(state: QuantityTiersEditorState) {
  return {
    component_type: "quantity_tiers" as const,
    layout: state.layout,
    tiers: state.tiers.map(buildTier),
    styles: {
      active_border_color: state.activeBorderColor || undefined,
      background_color: state.backgroundColor || undefined,
      selected_background_color: state.selectedBackgroundColor || undefined,
      border_radius: state.borderRadius,
    },
    hide_product_image: state.hideProductImage,
    hide_variant_quantity_selectors: state.hideVariantQuantitySelectors,
    disable_variant_selectors: state.disableVariantSelectors,
    use_compare_at_price_as_base: state.useCompareAtPriceAsBase,
    disable_if_exceeds_max_quantity: state.disableIfExceedsMaxQuantity,
  };
}

// ─── Public API ──────────────────────────────────────────────────────────────

export function stateToCreateRequest(
  state: QuantityTiersEditorState
): CreateOfferRequest {
  return {
    internal_name: state.internalName,
    is_active: state.isActive,
    priority: 0,
    config: {
      display_type: "quantity_tiers",
      display_data: buildDisplayData(state),
      pricing_logic: { action: "none" },
    },
    target: buildTarget(state),
    // Quantity-tier offers are intrinsically scoped to their target product:
    // the tiers' pricing is computed against that product's price and the
    // shopper sees the tiers above its OrderSummary line. Triggering on any
    // other product would discount a line whose base price wasn't priced into
    // the tiers, so we auto-derive a `specific_products` rule from the
    // picked target — no UI knob needed.
    triggers: {
      rule_type: "specific_products",
      product_ids: [state.targetProduct!.id],
    },
  };
}

export function stateToUpdateRequest(
  state: QuantityTiersEditorState,
  originalConfig?: OfferDetailResponse["config"] | null
): UpdateOfferRequest {
  const create = stateToCreateRequest(state);

  if (
    originalConfig?.display_type === "quantity_tiers" &&
    create.config?.display_type === "quantity_tiers"
  ) {
    return {
      ...create,
      config: {
        ...(originalConfig as Record<string, unknown>),
        ...create.config,
        display_data: {
          ...(originalConfig.display_data as Record<string, unknown>),
          ...create.config.display_data,
        } as typeof create.config.display_data,
      },
    };
  }

  return create;
}

export function offerToState(
  offer: OfferDetailResponse
): QuantityTiersEditorState {
  const config = offer.config;

  if (config.display_type !== "quantity_tiers") {
    throw new Error(
      `Este editor solo admite ofertas de tipo "quantity_tiers". La oferta "${offer.internal_name}" es de tipo "${config.display_type}".`
    );
  }

  const dd = config.display_data;
  const styles = (dd.styles ?? {}) as Record<string, unknown>;
  const defaults = getDefaultQuantityTiersState();

  const target = offer.target;
  const targetProduct: QuantityTiersTargetProduct | null =
    target.shopify_product_id
      ? {
          id: stripShopifyGid(target.shopify_product_id),
          title: target.display_meta?.title ?? "Producto",
          imageUrl: target.display_meta?.image_url,
          price: target.display_meta?.price ?? "0",
          compareAtPrice: target.display_meta?.compare_at_price,
          defaultVariantId: target.shopify_variant_id
            ? stripShopifyGid(target.shopify_variant_id)
            : undefined,
          variants: (target.display_meta?.variants ?? []).map((v) => ({
            id: v.id,
            title: v.title,
            ...(v.image_url ? { imageUrl: v.image_url } : {}),
          })),
        }
      : null;

  const tiers: QuantityTierEditorTier[] = dd.tiers.map((t) => {
    const p = t.pricing_logic;
    let pricingAction: QuantityTiersPricingAction = "none";
    let pricingAmount = 0;
    let pricingPercentage = 0;

    if (p.action === "fixed_price_per_item") {
      pricingAction = "fixed_price_per_item";
      pricingAmount = p.amount;
    } else if (p.action === "percentage_off") {
      pricingAction = "percentage_off";
      pricingPercentage = p.value;
    } else if (p.action === "fixed_amount_off") {
      pricingAction = "fixed_amount_off";
      pricingAmount = p.amount;
    }

    return {
      _tempId: crypto.randomUUID(),
      quantity: t.quantity,
      title: t.title,
      isDefault: t.is_default,
      hideCompareAtPrice: t.hide_compare_at_price,
      pricingAction,
      pricingAmount,
      pricingPercentage,
      badgeText: t.badge?.text ?? "",
      badgeTextColor: t.badge?.text_color ?? "#ffffff",
      badgeBackgroundColor: t.badge?.background_color ?? "#000000",
      priceColor: t.price_color ?? "#1f2937",
      titleColor: t.title_color ?? "#111827",
      customImageUrl: t.custom_image_url ?? "",
    };
  });

  return {
    internalName: offer.internal_name,
    isActive: offer.is_active,
    targetProduct,
    tiers,
    layout: dd.layout,
    activeBorderColor:
      (styles.active_border_color as string) ?? defaults.activeBorderColor,
    backgroundColor:
      (styles.background_color as string) ?? defaults.backgroundColor,
    selectedBackgroundColor:
      (styles.selected_background_color as string) ??
      defaults.selectedBackgroundColor,
    borderRadius: (styles.border_radius as number) ?? defaults.borderRadius,
    hideProductImage: dd.hide_product_image,
    hideVariantQuantitySelectors: dd.hide_variant_quantity_selectors,
    disableVariantSelectors: dd.disable_variant_selectors,
    useCompareAtPriceAsBase: dd.use_compare_at_price_as_base,
    disableIfExceedsMaxQuantity: dd.disable_if_exceeds_max_quantity,
  };
}
